/**
 * The live module registry — the demo/sandbox backing for the Module Manager.
 *
 * In a LIVE deployment this state is read from `core.installed_modules()` +
 * `core.entitlement()` and mutated only by the `solum` CLI (entitlement-gated,
 * topological, transactional) against a real Postgres. Here — running on the
 * fixture transport — it is a mutable, localStorage-persisted store so the modular
 * composition is testable END TO END in the browser: install/uninstall a module
 * and watch the nav, the entities, and the entitlement gate react live. The
 * governed rules (core is un-removable, deps must be present, a module must be
 * entitled to install) are enforced here exactly as the CLI enforces them.
 */
import { useEffect, useReducer } from 'react'
import { MODULES } from './modules'
import { consoleProfile } from './profile'

/**
 * The persisted-state key, VERSIONED — and exported so a test can assert the
 * version rather than eyeball it.
 *
 * localStorage outlives a deploy. Everyone who opened the console before the
 * seed was profiled has `controlplane` sitting in their persisted `installed`
 * array, so an unversioned key would keep the fleet gate open for exactly the
 * people it was written for. Bumping it discards that state.
 */
export const STORAGE_KEY = 'moonbase.console.registry.v2'
const KEY = STORAGE_KEY
interface State { installed: string[]; entitled: string[] }

/**
 * Does this console KNOW what the deployment is entitled to?
 *
 * In the fixture/sandbox it does: entitlement is the sandbox's own toggles, and
 * flipping one is the point of the surface. On a LIVE box it does not, and
 * `hydrate` used to answer the question by assuming — `entitled: [...installed]`
 * — which makes the licence unfalsifiable: every module is entitled because it
 * is there, so no module can ever be shown as unlicensed. A control that cannot
 * report a violation is not a control, and the real gate is the compiled
 * `<table>_license_gate` trigger (core 2026080522), not this.
 *
 * Nothing mints a licence yet — `issue_license` has no non-test caller — so
 * unknown is the true answer, and the surfaces say so rather than inventing one.
 */
let entitlementKnown = true

/**
 * The sandbox must exercise an actual licence refusal instead of relying on a
 * planned repository placeholder to remain forever. `marketing` is a real,
 * sellable module and is deliberately the one named module this demo licence
 * does not grant; a test can therefore address its card and prove the gate is
 * live while every manifest is free to become installable.
 */
export const SANDBOX_UNENTITLED_MODULES: ReadonlySet<string> = new Set(['marketing'])

/**
 * The seed is the CUSTOMER profile unless the build says otherwise (TEC-07).
 *
 * It used to seed everything that was not 'planned', which silently included
 * `controlplane` (state: 'internal') — so the fixture console, which is what a
 * customer evaluating the product actually sees, reported the control plane as
 * installed and lit up every fleet surface. Same defaulting rule as
 * `discoverManifests`: restrictive by omission, internal only by opting in.
 *
 * 'planned' ↔ zero migrations and 'internal' ↔ audience 'moonbase-internal' are
 * the console's spelling of `admitsModule`'s exclusions.
 */
const seed = (): State => {
  const internal = consoleProfile() === 'internal'
  const present = MODULES.filter((m) => m.state !== 'planned' &&
    !SANDBOX_UNENTITLED_MODULES.has(m.key) && (internal || m.state !== 'internal'))
  return {
    installed: present.map((m) => m.key),
    entitled: MODULES.filter((m) => m.entitled && !SANDBOX_UNENTITLED_MODULES.has(m.key) &&
      (internal || m.state !== 'internal')).map((m) => m.key),
  }
}
function load(): State {
  try { const raw = typeof localStorage !== 'undefined' && localStorage.getItem(KEY); if (raw) return JSON.parse(raw) as State } catch { /* node / private mode */ }
  return seed()
}

let state = load()
const listeners = new Set<() => void>()
function commit(next: State) {
  state = next
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(state)) } catch { /* ignore */ }
  listeners.forEach((l) => l())
}
const meta = (k: string) => MODULES.find((m) => m.key === k)

export const registry = {
  isInstalled: (k: string) => state.installed.includes(k),
  isEntitled: (k: string) => state.entitled.includes(k),
  installed: () => state.installed,
  /** Installed modules that depend on `k` (block its uninstall). */
  dependents: (k: string) => MODULES.filter((m) => m.deps.includes(k) && state.installed.includes(m.key)).map((m) => m.key),
  /** Deps of `k` not yet installed (block its install). */
  missingDeps: (k: string) => (meta(k)?.deps ?? []).filter((d) => !state.installed.includes(d)),

  /** Why `k` cannot be installed right now, or null if it can. */
  installBlocker: (k: string): string | null => {
    if (registry.isInstalled(k)) return 'already installed'
    if (!registry.isEntitled(k)) return 'not entitled — grant a license in Settings'
    const missing = registry.missingDeps(k)
    return missing.length ? `needs ${missing.join(', ')} installed first` : null
  },
  /** Why `k` cannot be uninstalled right now, or null if it can. */
  uninstallBlocker: (k: string): string | null => {
    if (k === 'core') return 'the kernel cannot be removed'
    if (!registry.isInstalled(k)) return 'not installed'
    const dep = registry.dependents(k)
    return dep.length ? `${dep.join(', ')} depend on it` : null
  },

  install(k: string) { if (!registry.installBlocker(k)) commit({ ...state, installed: [...state.installed, k] }) },
  uninstall(k: string) { if (!registry.uninstallBlocker(k)) commit({ ...state, installed: state.installed.filter((x) => x !== k) }) },
  setEntitled(k: string, v: boolean) {
    if (k === 'core') return
    const entitled = v ? [...new Set([...state.entitled, k])] : state.entitled.filter((x) => x !== k)
    // Revoking a license takes the module read-only in production; here we also drop it from installed.
    const installed = v ? state.installed : state.installed.filter((x) => x !== k)
    commit({ installed, entitled })
  },
  reset() { commit(seed()) },
  /** LIVE mode: reconcile to the real installed-modules ledger (`core.module`).
   *
   *  The keys are taken AS THE BOX REPORTS THEM. This used to filter them
   *  against `MODULES` — `installedKeys.filter((k) => known.has(k))` — which was
   *  the third and load-bearing static gate in #1615: a module correctly
   *  installed on the box but absent from this build's manifest inventory could
   *  never make `isInstalled` return true, so it was blanked from the Module
   *  Manager AND from the navigation, and no edit to the page alone could have
   *  restored it. What the box says is installed IS what is installed; whether
   *  this build can describe it is a separate question, answered in
   *  `moduleReconcile.ts`.
   *
   *  Deduplicated, because the state is a membership set and a door that
   *  repeated a name must not make it count twice.
   */
  hydrate(installedKeys: string[]) {
    // NOT `entitled: installed`. See `entitlementKnown` above — that shortcut is
    // what made the console report a licence it had never read.
    entitlementKnown = false
    commit({ installed: [...new Set(installedKeys)], entitled: [] })
  },
  /** False once `hydrate` has run — a live box, where no licence has been read. */
  entitlementKnown: () => entitlementKnown,
  subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l) } },
}

/** Re-render on any registry change. */
export function useRegistry() {
  const [, force] = useReducer((x: number) => x + 1, 0)
  useEffect(() => registry.subscribe(force), [])
  return registry
}
